import React from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Switch,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useFormActions, useForm, useMeta, useValidator } from "@/hooks";
import {
    MUTATION_UPDATE_CHILE_EXPRESS_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const ChileExpressForm = ({ app }) => {
    const { user } = useMeta();
    const { inputs, setInputs, inputChange } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                tcc: parseInt(inputs.tcc),
                test_tcc: parseInt(inputs.test_tcc),
                rut: parseInt(inputs.rut),
                test_rut: parseInt(inputs.test_rut),
                type: "ChileExpress",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "tcc",
                method: "isEmpty",
                validWhen: false,
                message: "This tcc is required.",
            },
            {
                field: "test_tcc",
                method: "isEmpty",
                validWhen: false,
                message: "This tcc is required.",
            },
            {
                field: "rut",
                method: "isEmpty",
                validWhen: false,
                message: "This rut is required.",
            },
            {
                field: "test_rut",
                method: "isEmpty",
                validWhen: false,
                message: "This rut is required.",
            },
            {
                field: "send_key",
                method: "isEmpty",
                validWhen: false,
                message: "This send key is required.",
            },
            {
                field: "test_send_key",
                method: "isEmpty",
                validWhen: false,
                message: "This send key is required.",
            },
            {
                field: "coverage_key",
                method: "isEmpty",
                validWhen: false,
                message: "This coverage key is required.",
            },
            {
                field: "test_coverage_key",
                method: "isEmpty",
                validWhen: false,
                message: "This coverage key is required.",
            },
            {
                field: "quote_key",
                method: "isEmpty",
                validWhen: false,
                message: "This quote key is required.",
            },
            {
                field: "test_quote_key",
                method: "isEmpty",
                validWhen: false,
                message: "This quote key is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "ChileExpress",
        "",
        MUTATION_UPDATE_CHILE_EXPRESS_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
                skip: user.loading || !user.accountId || !user.storeId,
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const tccField = inputs.sandbox ? "test_tcc" : "tcc";
    const rutField = inputs.sandbox ? "test_rut" : "rut";
    const sendKeyField = inputs.sandbox ? "test_send_key" : "send_key";
    const coverageKeyField = inputs.sandbox
        ? "test_coverage_key"
        : "coverage_key";
    const quoteKeyField = inputs.sandbox ? "test_quote_key" : "quote_key";

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Chile Express</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Chile Express Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Chile Express actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section title="Information">
                        <Switch
                            checked={Boolean(inputs.sandbox)}
                            label="Sandbox"
                            name="sandbox"
                            onChange={inputChange}
                        />
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, tccField)}
                                fullWidth
                                label="TCC"
                                name={tccField}
                                onChange={inputChange}
                                type="number"
                                value={inputs[tccField] || ""}
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, rutField)}
                                fullWidth
                                label="RUT"
                                name={rutField}
                                onChange={inputChange}
                                type="number"
                                value={inputs[rutField] || ""}
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, sendKeyField)}
                                fullWidth
                                label="Send key"
                                name={sendKeyField}
                                onChange={inputChange}
                                value={inputs[sendKeyField] || ""}
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, coverageKeyField)}
                                fullWidth
                                label="Coverage key"
                                name={coverageKeyField}
                                onChange={inputChange}
                                value={inputs[coverageKeyField] || ""}
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, quoteKeyField)}
                                fullWidth
                                label="Quote key"
                                name={quoteKeyField}
                                onChange={inputChange}
                                value={inputs[quoteKeyField] || ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default ChileExpressForm;
